/**
 * Текстові документи, які будуються з таблиці: розхід, список ПЗ, тіло рапорту.
 * Чиста логіка без введення-виведення — використовується і сервером, і ботом.
 */
import { addDays, dayMonth, rangeDates, shortDate } from './dates.js';
import { isCounted, mealKindAt, showsInExpense } from './marks.js';
import { nameForms, officialInitials, rankForm, shortName } from './people.js';
/* ------------------------------------------------------------ угруповання */
export const unitNameOf = (units, id) => units.find(u => u.id === id)?.name || '—';
const slug = (s) => s.replace(/[^\p{L}\p{N}]/gu, '') || '—';
/**
 * Ім'я файла документа: «Р+_Добове_Кулібаба.docx». Кирилицю лишаємо —
 * заголовок віддає ім'я в UTF-8, — прибираємо лише те, чого не можна в імені
 * файла на диску. Раніше тут стояло `\w`, а воно ASCII-only, тож від прізвища
 * лишалися самі підкреслення.
 */
export function docFileName(parts, ext) {
    const clean = parts
        .map(p => (p ?? '').trim().replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, '_'))
        .filter(Boolean);
    if (clean[0] !== 'Р+')
        clean.unshift('Р+');
    return `${clean.join('_')}.${ext}`;
}
/** Список на звільнення: «Р+_Список_звільнення_2026-09-17.docx». */
export const leaveListFileName = (date) => docFileName(['Список', 'звільнення', date], 'docx');
/** Іногородні до ранку: «Р+_Звільнені_до_ранку_іногородні_2026-10-03.docx». */
export const overnightListFileName = (date) => docFileName(['Звільнені', 'до', 'ранку', 'іногородні', date], 'docx');
/** Список ПЗ: «Р+_Список_ПЗ_2026-09-17.xlsx». */
export const canteenListFileName = (date) => docFileName(['Список', 'ПЗ', date], 'xlsx');
/** Місячна відомість: «Р+_Місячна_відомість_2026-04-01_2026-04-30.xlsx». */
export const monthTableFileName = (from, to) => docFileName(['Місячна', 'відомість', from, to], 'xlsx');
/** Рапорт на виплату — той самий шаблон, що й інші рапорти. */
export const bonusReportFileName = (from, to) => docFileName(['Р+', 'Виплата', from, to], 'docx');
/** Аркуш перевірки принтера. */
export const printerTestFileName = () => docFileName(['Перевірка', 'принтера'], 'docx');
/**
 * Підстава після «у зв'язку з …»: з малої літери і з крапкою в кінці,
 * якщо її ще немає. Порожнє лишає в бланку лінію.
 */
export function clauseReason(text) {
    const t = (text ?? '').trim();
    if (!t || /^_+$/.test(t))
        return '';
    const body = t.charAt(0).toLocaleLowerCase('uk') + t.slice(1);
    return /[.!?…]$/u.test(body) ? body : `${body}.`;
}
/**
 * Люди підрозділу, згруповані за ним же. Знімає з харчування не ознака в
 * картці, а позначка в таблиці (відпустка, компенсація) — тому тут лише
 * активні, а хто саме сьогодні йде в їдальню, вирішує `eatsHere`.
 */
export function byUnit(ctx) {
    const on = ctx.people.filter(p => p.active);
    const names = [...new Set(on.map(p => unitNameOf(ctx.units, p.unitId)))].sort((a, b) => a.localeCompare(b, 'uk'));
    return names.map(unit => ({
        unit,
        people: on.filter(p => unitNameOf(ctx.units, p.unitId) === unit),
    }));
}
const kindOf = (ctx, p, d, meal) => mealKindAt(ctx.index.person(p.id), d, meal);
const isCompensated = (ctx, p, d, meal) => isCounted(ctx.markTypes, kindOf(ctx, p, d, meal));
/**
 * Чи йде людина в їдальню на цей прийом їжі. Без позначки — йде; позначка з
 * `eatsInCanteen: false` (компенсація, відпустка) виводить її зі списку.
 */
const eatsHere = (ctx, p, d, meal) => {
    const kind = kindOf(ctx, p, d, meal);
    const type = kind ? ctx.markTypes.find(t => t.code === kind) : undefined;
    return !type || type.eatsInCanteen;
};
/** Текст розходу: скільки людей із кожної групи їсть у їдальні. */
export function expenseNote(ctx, from, to, opt = { showZero: true }) {
    const groups = byUnit(ctx);
    const blocks = [];
    for (const d of rangeDates(from, to)) {
        ctx.meals.forEach((meal, ml) => {
            let total = 0;
            let comp = 0;
            const lines = [`${meal.name} ${shortDate(d)}`];
            for (const g of groups) {
                const marked = g.people.filter(p => isCompensated(ctx, p, d, ml)).length;
                const eat = g.people.filter(p => eatsHere(ctx, p, d, ml)).length;
                total += eat;
                comp += marked;
                lines.push(`${slug(g.unit)}-${eat}`);
            }
            lines.push(`Всього: ${total}`);
            if (comp || opt.showZero)
                lines.push(`Компенсація: ${comp}`);
            blocks.push(lines.join('\n'));
        });
    }
    return blocks.join('\n\n');
}
/**
 * Один рядок розходу. Винесено окремо, щоб налаштування показували зразок
 * тим самим складальником, яким друкується справжній документ, — інакше
 * зразок і розхід розходяться від першої ж правки.
 */
export function expenseTallyLine(meal, eat, groups, tall, style) {
    const away = groups
        .filter(g => g.count)
        .map(g => ` (${g.count}${g.letter}${style === 'away' && g.tall ? `, з них ${g.tall} 190+` : ''})`)
        .join('');
    return `${meal} - ${eat}${away}${style === 'eating' && tall ? ` (${tall} 190+)` : ''}`;
}
/**
 * «Розхід ВМЗ» за один день — те, що черговий диктує на кухню.
 *
 * Перше число — скільки порцій готувати. Далі в дужках ті, хто в їдальню не
 * йде: компенсація, відпустка тощо, літерою свого виду позначки. Порожні
 * групи не пишемо. Різняться два вигляди тим, чиї «190+» рахувати.
 *
 * `eating` — тих, хто **їсть**: їм належить збільшена норма, тож число
 * стоїть окремими дужками в кінці рядка.
 *
 * ```
 * 08.09
 * Сніданок - 29 (34К) (3 190+)
 * Обід - 63 (8 190+)
 * Вечеря - 4 (59К)
 * ```
 *
 * `away` — тих, хто **не** їсть, і окремо в кожній групі: число стоїть у
 * дужках своєї групи, після коми.
 *
 * ```
 * 10.09
 * Сніданок - 31 (32К, з них 4 190+)
 * Обід - 63
 * Вечеря - 3 (60К, з них 8 190+)
 * ```
 */
export function expenseTally(ctx, date, style = 'eating') {
    const people = ctx.people.filter(p => p.active);
    const lines = [shortDate(date)];
    ctx.meals.forEach((meal, ml) => {
        const eat = people.filter(p => eatsHere(ctx, p, date, ml));
        const groups = ctx.markTypes
            .filter(t => !t.eatsInCanteen && showsInExpense(t))
            .map(t => {
            const group = people.filter(p => kindOf(ctx, p, date, ml) === t.code);
            return {
                letter: t.letter.toLocaleUpperCase(ctx.locale || 'uk'),
                count: group.length,
                tall: group.filter(p => p.tall).length,
            };
        });
        lines.push(expenseTallyLine(meal.name, eat.length, groups, eat.filter(p => p.tall).length, style));
    });
    return lines.join('\n');
}
/** Хто фактично харчується в їдальні за конкретну дату. */
export function canteenList(ctx, date) {
    const groups = byUnit(ctx);
    const out = [];
    ctx.meals.forEach((meal, ml) => {
        const blocks = [];
        let total = 0;
        let reports = 0;
        for (const g of groups) {
            const eat = g.people.filter(p => eatsHere(ctx, p, date, ml));
            reports += g.people.filter(p => isCompensated(ctx, p, date, ml)).length;
            if (eat.length)
                blocks.push({ unit: g.unit, people: eat });
            total += eat.length;
        }
        if (reports) {
            const [y, m, d] = date.split('-');
            out.push({ meal: ml, title: `${meal.name} ${d}.${m}.${y}`, total, reports, blocks });
        }
    });
    return out;
}
/* ---------------------------------------------------------------- рапорт */
/**
 * Прийоми їжі, які йдуть у рапорт людини: `[дата, прийом]`. Те саме, з чого
 * `mealsPhrase` складає текст, — журнал видач зберігає саме це, щоб потім
 * звірити виданий рапорт із таблицею й розходом поклітинно.
 */
export function compensatedCells(ctx, person, from, to) {
    const out = [];
    for (const d of rangeDates(from, to)) {
        ctx.meals.forEach((_, i) => {
            if (isCompensated(ctx, person, d, i))
                out.push([d, i]);
        });
    }
    return out;
}
/**
 * Знімок розходу на дату: на кожен прийом їжі — хто на компенсації і скільки
 * порцій. Розхід — лише числа, тож без переліку людей його потім не звірити
 * з рапортами.
 */
export function expenseSnapshot(ctx, date) {
    const people = ctx.people.filter(p => p.active);
    return ctx.meals.map((_, ml) => ({
        eat: people.filter(p => eatsHere(ctx, p, date, ml)).length,
        comp: people.filter(p => isCompensated(ctx, p, date, ml)).map(p => p.id),
    }));
}
/** «сніданок, вечерю у період з 01 по 05 березня 2026 року» */
/**
 * «сніданок, обід у період з 02 по 05 березня 2026 року».
 *
 * Формат до знака збігається з першою версією: обидва числа з нулем попереду,
 * місяць береться за початком проміжку, а самі проміжки не переходять через
 * межу місяця — так документ читається однаково рік у рік.
 */
export function mealsPhrase(ctx, person, from, to) {
    const segs = [];
    for (const d of rangeDates(from, to)) {
        const ms = ctx.meals.map((_, i) => i).filter(i => isCompensated(ctx, person, d, i));
        if (!ms.length)
            continue;
        const sig = ms.join(',');
        const last = segs[segs.length - 1];
        const sameMonth = last && last.to.slice(0, 7) === d.slice(0, 7);
        if (last && last.sig === sig && sameMonth && addDays(last.to, 1) === d)
            last.to = d;
        else
            segs.push({ sig, meals: ms, from: d, to: d });
    }
    if (!segs.length)
        return '';
    const monthOf = (d) => dayMonth(d, ctx.locale).replace(/^\d+\s+/, '');
    const text = segs
        .map(g => {
        const names = g.meals.map(i => ctx.meals[i]?.accusative ?? '').join(', ');
        const month = monthOf(g.from);
        if (g.from === g.to)
            return `${names} ${g.from.slice(8)} ${month}`;
        return `${names} у період з ${g.from.slice(8)} по ${g.to.slice(8)} ${month}`;
    })
        .join(', ');
    return `${text} ${segs[segs.length - 1].to.slice(0, 4)} року`;
}
/** Посада людини; якщо не вказана — перша у довіднику (зазвичай рядова). */
export const positionOf = (positions, code) => positions.find(p => p.code === code) ?? positions[0];
/**
 * Номер групи разом зі словом: «С-21 групи ». У старшини курсу групи немає —
 * він старшина цілого курсу, — тож у його рапорті зникає не лише номер, а й
 * саме слово: «Старшина С2 курсу Магістерського курсу».
 */
export const unitPhrase = (ctx, person) => person.positionCode === 'master' ? '' : `${unitNameOf(ctx.units, person.unitId)} групи `;
/**
 * «{UNIT} групи» в бланку — це одне ціле, а не ключ і сусіднє слово. Поки
 * група була в кожного, різниці не було; у старшини ж порожній {UNIT} лишав
 * по собі «Старшина  групи С2 курсу». Тому така пара зводиться до одного
 * ключа {UNIT_PHRASE}, який слово «групи» вже містить у собі.
 */
export const unitWithWord = (template) => template.replace(/\{(?:UNIT|GROUP)\}[ \u00A0]+груп(?:и|і|у|а)(?![\p{L}])[ \u00A0]*/gu, '{UNIT_PHRASE}');
/** Значення для підстановки у шаблон рапорту. */
export function reportPlaceholders(ctx, person, from, to) {
    const forms = nameForms(person);
    const pos = positionOf(ctx.positions, person.positionCode);
    return {
        MEALS: mealsPhrase(ctx, person, from, to) || '—',
        ROLE: pos?.name ?? '',
        ROLE_DAT: pos?.nameDat ?? '',
        UNIT: unitNameOf(ctx.units, person.unitId),
        GROUP: unitNameOf(ctx.units, person.unitId),
        UNIT_PHRASE: unitPhrase(ctx, person),
        COURSE: ctx.course,
        RANK: rankForm(ctx.ranks, person.rankCode, 'name'),
        RANK_DAT: rankForm(ctx.ranks, person.rankCode, 'dat'),
        RANK_GEN: rankForm(ctx.ranks, person.rankCode, 'gen'),
        RANK_SHORT: rankForm(ctx.ranks, person.rankCode, 'short'),
        NAME_DAT: forms.dat,
        NAME_NOM: forms.nom,
        NAME_GEN: forms.gen,
        NAME_SHORT: shortName(person),
        TAX_CODE: person.taxCode,
        FROM: dayMonth(from, ctx.locale),
        TO: dayMonth(to, ctx.locale),
    };
}
export function applyPlaceholders(template, values) {
    return unitWithWord(template).replace(/\{([A-Z_]+)\}/g, (m, k) => values[k] ?? m);
}
/** Посадовці, які можуть стояти в цьому блоці документа. */
export const officialsOf = (officials, kind) => officials.filter(o => o.active && o.roles.some(r => r.kind === kind)).sort((a, b) => a.ord - b.ord);
/** Як саме ця людина називається в конкретному блоці. */
export const roleOf = (official, kind) => official?.roles.find(r => r.kind === kind);
/** Усі групи, у яких хтось є, — для переліку блоків у документах. */
export const officialKindsUsed = (officials) => [
    ...new Set(officials.flatMap(o => o.roles.map(r => r.kind))),
];
export const officialLine = (o) => o ? `${o.rank} ${officialInitials(o)} ${o.lastName}`.replace(/\s+/g, ' ').trim() : '';
/**
 * Той самий рядок, але з посадою саме в цій ролі: діловоду має бути видно,
 * стане в документі «Начальник» чи «Т.в.о. начальника», і заступник чого це —
 * уточнення «(з навчальної роботи)» йде одразу за посадою.
 */
export const officialRoleLine = (o, kind) => {
    const role = roleOf(o, kind);
    const position = [role?.position, role?.note].filter(Boolean).join(' ');
    return [officialLine(o), position].filter(Boolean).join(' — ');
};
//# sourceMappingURL=documents.js.map